import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { BridgeMark } from '../components/icons/Svg';
import { Button } from '../ui/Button';
import { Input, Label } from '../ui/Input';
import { cn } from '../lib/cn';
import { rememberMe, setRememberMe } from '../api/client';

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [remember, setRemember] = useState(() => rememberMe());

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    // Persist the choice BEFORE calling login so setTokens writes to the
    // correct storage on this request. Uncheck = sessionStorage (gone on
    // browser close); check = localStorage (30d refresh).
    setRememberMe(remember);
    try {
      await login(email, password);
      nav('/');
    } catch (ex) {
      setErr(ex?.response?.data?.detail || 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-[hsl(var(--bg))] px-4 text-[hsl(var(--fg))]">
      {/* subtle breathing glow behind the card, same language as the brief hub */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-1/2 -z-0 h-[560px] w-[560px] -translate-x-1/2 -translate-y-1/2 rounded-full blur-3xl animate-brief-breathe"
        style={{
          background:
            'radial-gradient(closest-side, hsl(var(--primary) / 0.14), transparent 70%)',
        }}
      />
      <form
        className={cn(
          'relative z-10 flex w-full max-w-md flex-col gap-5',
          'rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-8 shadow-elev-3'
        )}
        onSubmit={submit}
      >
        <div className="flex items-center gap-3">
          <BridgeMark size={36} />
          <h1 className="m-0 text-xl font-semibold tracking-tight">BridgeIntoTax</h1>
        </div>
        <p className="text-sm text-[hsl(var(--fg-muted))]">
          AI-powered document portal for tax professionals.
        </p>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="login-email">Email</Label>
          <Input
            id="login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
            required
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="login-password">Password</Label>
            <Link
              to="/forgot-password"
              className="text-[11px] text-[hsl(var(--fg-muted))] hover:text-[hsl(var(--fg))]"
            >
              Forgot password?
            </Link>
          </div>
          <Input
            id="login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>

        <label className="-mt-1 flex items-center gap-2 text-[12px] text-[hsl(var(--fg-muted))]">
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            className="h-4 w-4 accent-[hsl(var(--primary))]"
          />
          Remember me on this device
          <span
            className="ml-auto text-[10px]"
            title={
              remember
                ? 'Stays signed in for 30 days'
                : 'Signed out when this tab closes'
            }
          >
            {remember ? '30 days' : 'this session'}
          </span>
        </label>

        {err && (
          <div
            role="alert"
            className="rounded-lg border border-[hsl(var(--danger)/0.35)] bg-[hsl(var(--danger)/0.1)] px-3 py-2 text-sm text-[hsl(var(--fg))]"
          >
            {err}
          </div>
        )}

        <Button type="submit" disabled={busy} className="w-full">
          {busy ? 'Signing in' : 'Log in'}
        </Button>
      </form>
    </div>
  );
}
